<template>
    <v-container class="py-7">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span class="page-title">{{ isNew ? 'Nova importação' : 'Importação' }}</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbItems" />
                    </div>
                    <div class="button-group">
                        <v-btn v-if="isNew && $check('Creditors', 'POST')" color="primary" elevation="0"
                            @click="submitImport" :loading="loading">
                            Enviar importação
                        </v-btn>
                        <v-btn color="primary" outlined elevation="0" @click="goBack">Voltar</v-btn>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="isNew">
            <v-col cols="12">
                <v-card class="card-form-outlined import-card">
                    <div class="d-flex align-center justify-space-between mb-4">
                        <div class="import-card__title">Arquivo do QGC</div>
                        <v-btn small text color="primary" @click="dialogFormat = true">
                            <v-icon left small>mdi-help-circle-outline</v-icon> Formato e modelos de planilha
                        </v-btn>
                    </div>

                    <v-form ref="form">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-autocomplete v-model="form.client_id" :items="clients" item-text="label"
                                    item-value="value" label="Caso" outlined dense hide-details="auto"
                                    no-data-text="Nenhum caso encontrado" :rules="[rules.required]" />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select v-model="form.version" :items="versionOptions" item-text="label"
                                    item-value="id" label="Versão do QGC" outlined dense hide-details="auto"
                                    :rules="[rules.required]" />
                            </v-col>
                            <v-col cols="12">
                                <v-file-input v-model="form.file" label="Planilha (CSV ou Excel, até 20 MB)" show-size
                                    outlined dense hide-details="auto" prepend-icon="" prepend-inner-icon="mdi-paperclip"
                                    @change="onFileChange" accept=".csv,.xlsx,.xls"
                                    :rules="[rules.required, rules.fileSize(20)]" />
                            </v-col>
                        </v-row>

                        <div class="d-flex align-center mt-4">
                            <v-btn outlined color="primary" small @click="validatePreview">Validar arquivo</v-btn>
                            <span class="import-card__hint ml-3">Confere as colunas antes de enviar. A validação completa é feita no envio.</span>
                        </div>

                        <v-progress-linear v-if="progress !== null" v-model="progress" color="success" height="22"
                            reactive rounded class="mt-4">
                            <strong class="white--text">{{ progress }}%</strong>
                        </v-progress-linear>
                    </v-form>
                </v-card>
            </v-col>
        </v-row>

        <!-- MODAL FORMATO DO ARQUIVO -->
        <v-dialog v-model="dialogFormat" max-width="900px">
            <v-card>
                <v-card-title class="font-weight-bold">
                    Formato do Arquivo
                </v-card-title>

                <v-card-text class="black--text">
                    <p>
                        Aceitamos arquivos nos formatos <strong>CSV</strong> ou <strong>XLSX</strong>.
                        Para CSV, são suportados delimitadores <strong>vírgula</strong> ou
                        <strong>ponto-e-vírgula</strong>.
                        Para planilhas XLSX, a validação completa será feita no servidor.
                    </p>

                    <p class="mt-4">
                        As colunas esperadas (mapeadas para o sistema) são:
                    </p>

                    <ul>
                        <li><strong>Nome / Nome completo:</strong> nome do credor. Ex.: João Silva</li>
                        <li>
                            <strong>CPF/CNPJ / Registro:</strong> CPF ou CNPJ do credor (com ou sem formatação).
                            <br />
                            Observação: CPF tem 11 dígitos; CNPJ tem 14 dígitos. Máscaras serão removidas
                            automaticamente.
                        </li>
                        <li>
                            <strong>Classe:</strong> identificador numérico da classe (ex.: 1,2,3) ou texto legível
                            (Classe I - Trabalhista, Classe II - Garantia Real, ...). A coluna no arquivo pode se chamar
                            <em>tipo</em> ou <em>classe</em>.
                        </li>
                        <li>
                            <strong>Valor:</strong> valor do crédito (número, pode usar vírgula ou ponto decimal).
                        </li>
                        <li><strong>Moeda:</strong> código/identificador de moeda (opcional, padrão = 1)</li>
                        <li><strong>E-mail:</strong> e-mail do credor (opcional)</li>
                    </ul>

                    <p class="mt-4">
                        <strong>Exemplos de cabeçalhos aceitos (os nomes são mapeados automaticamente):</strong>
                    </p>

                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>CSV / XLSX header</th>
                                <th>Mapeia para</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Nome | Nome completo</td>
                                <td>name</td>
                            </tr>
                            <tr>
                                <td>CPF/CNPJ | registro | cpf | cnpj</td>
                                <td>registration</td>
                            </tr>
                            <tr>
                                <td>Tipo | tipo | Classe | classe</td>
                                <td>type</td>
                            </tr>
                            <tr>
                                <td>Valor | valor | amount</td>
                                <td>amount</td>
                            </tr>
                            <tr>
                                <td>Moeda | currency</td>
                                <td>currency</td>
                            </tr>
                            <tr>
                                <td>E-mail | email</td>
                                <td>email</td>
                            </tr>
                        </tbody>
                    </v-simple-table>

                    <v-row class="mt-4">
                        <v-col cols="12" md="6">
                            <v-btn color="primary" @click="downloadTemplate" small>
                                Baixar template (.csv)
                            </v-btn>
                            <v-btn color="primary" class="ml-2" @click="downloadTemplateXlsx" small>
                                Baixar template (.xlsx)
                            </v-btn>
                        </v-col>
                    </v-row>

                    <p class="mt-4 font-weight-bold">
                        Exemplo de registros de um arquivo válido:
                    </p>

                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>CPF/CNPJ</th>
                                <th>Tipo</th>
                                <th>Valor</th>
                                <th>E-mail</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>João Silva</td>
                                <td>880.732.328-12</td>
                                <td>1</td>
                                <td>6292,29</td>
                                <td>joaosilva@example.com</td>
                            </tr>
                            <tr>
                                <td>Bianca Siqueira</td>
                                <td>30436901412</td>
                                <td>2</td>
                                <td>5982,00</td>
                                <td>bianca@example.com</td>
                            </tr>
                            <tr>
                                <td>Pedro Alencar</td>
                                <td>69.972.491/0001-39</td>
                                <td>3</td>
                                <td>5142</td>
                                <td>pedroalencar@example.com</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card-text>

                <v-card-actions class="justify-end">
                    <v-btn text color="primary" @click="dialogFormat = false">
                        Fechar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Resultado (após o envio ou ao abrir uma importação existente) -->
        <v-row v-if="importResult">
            <v-col cols="12">
                <v-card class="card-form-outlined import-card">
                    <div class="d-flex align-center justify-space-between mb-4">
                        <div class="import-card__title">Resultado</div>
                        <v-btn small text color="primary" @click="fetchImportDetails(importResult.id)">
                            <v-icon left small>mdi-refresh</v-icon> Atualizar
                        </v-btn>
                    </div>
                    <div class="import-info">
                        <div class="import-info__item">
                            <span class="import-info__label">Situação</span>
                            <span><v-chip small label text-color="white" :color="statusInfo.color">{{ statusInfo.name }}</v-chip></span>
                        </div>
                        <div v-if="importResult.filename" class="import-info__item">
                            <span class="import-info__label">Arquivo</span>
                            <span class="import-info__value">{{ importResult.filename }}</span>
                        </div>
                        <div v-if="importResult.client" class="import-info__item">
                            <span class="import-info__label">Caso</span>
                            <span class="import-info__value">{{ importResult.client.name }}</span>
                        </div>
                        <div class="import-info__item">
                            <span class="import-info__label">Versão do QGC</span>
                            <span class="import-info__value">{{ (importResult.version || form.version) | enum(versionOptions, 'id', 'label') }}</span>
                        </div>
                        <div class="import-info__item">
                            <span class="import-info__label">Linhas</span>
                            <span class="import-info__value">{{ importResult.total_rows || 0 }}</span>
                        </div>
                        <div v-if="importResult.created_at" class="import-info__item">
                            <span class="import-info__label">Enviado em</span>
                            <span class="import-info__value">{{ importResult.created_at | defaultDate('DD/MM/YYYY HH:mm') }}</span>
                        </div>
                    </div>
                    <p v-if="importResult.status === 4 && !importErrors.length" class="import-card__hint error--text mt-4 mb-0">
                        O arquivo não pôde ser lido: faltam colunas obrigatórias (nome, CPF/CNPJ, classe e valor) ou o
                        formato é inválido. Confira o modelo em "Nova importação" e envie de novo.
                    </p>
                    <p v-if="importResult.status === 1 || importResult.status === 2" class="import-card__hint mt-4 mb-0">
                        O processamento leva alguns instantes. Use "Atualizar" para ver o resultado.
                    </p>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="importErrors.length">
            <v-col cols="12">
                <v-card class="card-form-outlined import-card import-card--error">
                    <div class="import-card__title error--text mb-1">
                        <v-icon color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                        Linhas com erro ({{ importErrors.length }})
                    </div>
                    <p class="import-card__hint">Nada foi importado. Corrija as linhas abaixo na planilha e envie de novo.</p>
                    <v-simple-table dense class="app-table">
                        <thead>
                            <tr>
                                <th class="text-left" style="width: 90px">Linha</th>
                                <th class="text-left">Erro</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(e, idx) in importErrors.slice(0, 200)" :key="idx">
                                <td class="font-weight-bold">{{ e.line_number || e.row || '-' }}</td>
                                <td>{{ formatImportError(e) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios'
import qgcVersions from '@/common/data/qgc-versions.data.js'

const defaultQgcVersion = (qgcVersions[0] && qgcVersions[0].id) || 1;

export default {
    name: "CreditorsDetails",

    data: () => ({
        dialogFormat: false,

        form: {
            name: null,
            case: null,
            email: null,
            file: null,
            client_id: null,
            version: defaultQgcVersion,
        },

        clients: [],
        importResult: null,
        importErrors: [],
        _fileExt: null,
        validationErrors: [],
        loading: false,
        progress: null,

        versionOptions: qgcVersions,
        rules: {
            required: v => !!v || 'Campo obrigatório.',
            email: v => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'E-mail inválido.',
            fileSize: size => file => {
                const currentFile = Array.isArray(file) ? file[0] : file
                return !currentFile || currentFile.size <= size * 1024 * 1024 || `Arquivo deve ser inferior a ${size} MB`
            },
        },

        caseOptions: ["Empresa 1", "Empresa 2", "Empresa 3"],
    }),

    computed: {
        isNew() {
            return !this.$route.params.id || this.$route.params.id === 'novo';
        },

        breadcrumbItems() {
            return [
                { text: "Início", to: "/home", exact: true },
                { text: "Importação QGC", to: "/lista-credores", exact: true },
                { text: this.isNew ? "Nova importação" : "Detalhes", disabled: true },
            ];
        },

        statusInfo() {
            return {
                1: { name: 'Aguardando processamento', color: 'warning' },
                2: { name: 'Processando', color: 'info' },
                3: { name: 'Concluída', color: 'success' },
                4: { name: 'Com erros', color: 'error' },
            }[this.importResult && this.importResult.status] || { name: '—', color: 'grey' };
        },
    },

    // A mesma tela atende "nova" e "existente": ao trocar de uma para a outra, recomeça do zero.
    watch: {
        '$route.params.id'() {
            this.importResult = null
            this.importErrors = []
            this.progress = null
            this.form = { ...this.form, file: null, client_id: null, version: defaultQgcVersion }
            if (!this.isNew) this.fetchImportDetails(this.$route.params.id)
        },
    },

    created() {
        this.loadClients()

        // Abrindo uma importação existente: mostra o resultado e as linhas com erro.
        if (this.$route.params.id && this.$route.params.id !== 'novo') this.fetchImportDetails(this.$route.params.id)
    },

    methods: {
        save() {
            console.log("Salvar", this.form);
        },

        goBack() {
            this.$router.push("/lista-credores");
        },

        async loadClients() {
            try {
                const res = await axios.get('/api/clients?page=1&limit=500')
                const data = res.data && res.data.content ? res.data.content.data : res.data
                this.clients = (data && data.rows) ? data.rows.map(c => ({ label: c.name || c.fantasy_name || c.id, value: c.id })) : (Array.isArray(data) ? data.map(c => ({ label: c.name || c.id, value: c.id })) : [])
            } catch (err) {
                console.error('Erro ao carregar clients', err)
                this.clients = []
            }
        },

        downloadTemplate() {
            const header = '"Nome completo","CPF/CNPJ","classe","valor","moeda","email"\n'
            const lines = [
                '"Empresa A","00000000000000000001","Classe I - Trabalhista",10000.00,1,empresaA@exemplo.com',
                '"Empresa B","00000000000000000002","Classe II - Garantia Real",25000.50,1,empresaB@exemplo.com',
            ]
            const csv = header + lines.join('\n')
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'creditor_claims_template.csv'
            a.click()
            URL.revokeObjectURL(url)
        },

        async downloadTemplateXlsx() {
            try {
                // dynamic import to avoid bundling xlsx if not needed
                const XLSX = await import('xlsx')


                const header = ["Nome completo", "CPF/CNPJ", "classe", "valor", "moeda", "email"]
                const rows = [
                    ["Empresa A", "00000000001", "Classe I - Trabalhista", 10000.00, 1, "empresaA@exemplo.com"],
                    ["Empresa B", "00000000002", "Classe II - Garantia Real", 25000.50, 1, "empresaB@exemplo.com"]
                ]

                const wsData = [header, ...rows]
                const ws = XLSX.utils.aoa_to_sheet(wsData)
                const wb = XLSX.utils.book_new()
                XLSX.utils.book_append_sheet(wb, ws, 'template')

                // add a second sheet with accepted classes for convenience
                try {
                    const classes = await import('../../common/data/creditor-types.data.js')
                    const classRows = [["id", "classe"]]
                    classes.default.forEach(c => classRows.push([c.id, c.name]))
                    const ws2 = XLSX.utils.aoa_to_sheet(classRows)
                    XLSX.utils.book_append_sheet(wb, ws2, 'classes')
                } catch (e) {
                    // ignore if cannot load; primary template remains
                }

                const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
                const blob = new Blob([wbout], { type: 'application/octet-stream' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = 'creditor_claims_template.xlsx'
                a.click()
                URL.revokeObjectURL(url)
            } catch (err) {
                console.error('Erro ao gerar template xlsx', err)
                this.$dialog.notify.error(this.$codes(err) || 'Erro ao gerar template XLSX', { position: 'top-right', timeout: 5000 })
            }
        },

        onFileChange(file) {
            this.validationErrors = []
            this.importErrors = []
            this.importResult = null
            this.progress = null
            this._fileExt = null
            if (!file) return
            const f = Array.isArray(file) ? file[0] : file
            if (!f) return

            // infer extension from name or MIME
            const name = f.name || ''
            const extMatch = name.toLowerCase().match(/\.([a-z0-9]+)$/)
            const ext = extMatch ? extMatch[1] : (f.type && f.type.includes('spreadsheet') ? 'xlsx' : null)
            this._fileExt = ext

            // If XLSX/XLS, we cannot reliably validate CSV headers client-side without a library.
            if (ext === 'xlsx' || ext === 'xls') {
                // keep validationErrors empty and inform user when previewing
                return
            }

            // For CSV files, read first line and validate headers
            const reader = new FileReader()
            reader.onload = (e) => {
                const text = e.target.result
                // detect delimiter ; or ,
                const firstLine = text.split(/\r?\n/)[0]
                const delimiter = firstLine.indexOf(';') !== -1 ? ';' : ','
                const headers = firstLine.split(delimiter).map(h => h.replace(/"/g, '').trim().toLowerCase())
                const hasName = headers.some(h => ['name', 'nome', 'nome completo'].includes(h))
                const hasRegistration = headers.some(h => ['registration', 'registro', 'cpf/cnpj', 'cpf', 'cnpj'].includes(h))
                const hasClass = headers.some(h => ['tipo', 'type', 'classe', 'class'].includes(h))
                if (!hasName) this.validationErrors.push('Cabeçalho ausente: Nome / Nome completo')
                if (!hasRegistration) this.validationErrors.push('Cabeçalho ausente: CPF/CNPJ / registro')
                if (!hasClass) this.validationErrors.push('Cabeçalho ausente: Tipo / Classe')
            }
            reader.readAsText(f)
        },

        validatePreview() {
            if (!this.form.file) {
                this.$dialog.notify.error('Selecione um arquivo primeiro', { position: 'top-right', timeout: 3000 })
                return
            }
            // If XLSX/XLS, inform user that preview validation is not available client-side
            if (this._fileExt === 'xlsx' || this._fileExt === 'xls') {
                if (this.$dialog && this.$dialog.notify && this.$dialog.notify.info) {
                    this.$dialog.notify.info('Arquivo XLSX detectado — validação completa será feita no servidor.', { position: 'top-right', timeout: 5000 })
                } else {
                    console.info('XLSX detected — server will validate file')
                }
                return
            }

            if (this.validationErrors.length) {
                if (this.$dialog && this.$dialog.notify && this.$dialog.notify.error) {
                    this.$dialog.notify.error(this.validationErrors.join('; '), { position: 'top-right', timeout: 5000 })
                } else {
                    console.warn('notify.error not available', this.validationErrors)
                }
                return
            }
            if (this.$dialog && this.$dialog.notify && this.$dialog.notify.success) {
                this.$dialog.notify.success('Arquivo parece válido (verificação básica)', { position: 'top-right', timeout: 5000 })
            } else {
                console.info('Arquivo parece válido (verificação básica)')
            }
        },

        async submitImport() {
            if (!this.$refs.form.validate()) return

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja enviar a importação do QGC?',
                title: 'Importação QGC',
                actions: { false: 'Cancelar', true: 'Enviar' }
            })

            if (!proceedAction) return

            if (this.validationErrors.length) {
                if (this.$dialog && this.$dialog.notify && this.$dialog.notify.error) {
                    this.$dialog.notify.error('Corrija os erros antes de enviar: ' + this.validationErrors.join('; '), { position: 'top-right', timeout: 5000 })
                }
                return
            }

            try {
                this.loading = true
                this.progress = 0
                this.importErrors = []
                this.importResult = null
                const fd = new FormData()
                const f = Array.isArray(this.form.file) ? this.form.file[0] : this.form.file
                fd.append('file', f)
                fd.append('client_id', this.form.client_id)
                fd.append('version', this.form.version)

                // let the browser set Content-Type (with boundary) for FormData
                const res = await axios.post('/api/admin/creditor-claims/imports', fd, {
                    onUploadProgress: (event) => {
                        if (!event.total) return
                        this.progress = Math.round((100 * event.loaded) / event.total)
                    }
                })
                const responseData = res.data && (res.data.content || res.data.data || res.data)
                const errors = this.extractImportErrors(responseData)

                if (errors.length) {
                    this.importErrors = errors
                    this.progress = null
                    this.$dialog.notify.error('Encontramos alguns erros na lista enviada, verifique-os abaixo!', { position: 'top-right', timeout: 4000 })
                    return
                }

                if (res.data && res.data.status) {
                    this.importResult = responseData
                    this.$dialog.notify.success('Importação iniciada com sucesso. Aguarde o processamento.', { position: 'top-right', timeout: 5000 })
                } else {
                    if (this.$dialog && this.$dialog.notify && this.$dialog.notify.error) {
                        this.$dialog.notify.error('Erro ao iniciar importação', { position: 'top-right', timeout: 5000 })
                    }
                }
            } catch (err) {
                console.error('submitImport error', err)
                this.progress = null
                const responseData = err.response && err.response.data
                const errors = this.extractImportErrors(responseData && (responseData.content || responseData.data || responseData))
                if (errors.length) {
                    this.importErrors = errors
                    this.$dialog.notify.error('Encontramos alguns erros na lista enviada, verifique-os abaixo!', { position: 'top-right', timeout: 4000 })
                    return
                }

                const msg = this.extractErrorMessage(err)
                // Prefer project codes helper if available
                try {
                    if (this.$dialog && this.$dialog.notify && this.$dialog.notify.error) {
                        // if $codes exists, use it to translate the error, otherwise fallback to msg
                        const payload = (typeof this.$codes === 'function') ? this.$codes(err) : (msg || 'Erro ao enviar importação')
                        this.$dialog.notify.error(payload, { position: 'top-right', timeout: 5000 })
                    } else {
                        console.error(msg || 'Erro ao enviar importação')
                    }
                } catch (notifyErr) {
                    console.error('notify error', notifyErr)
                }
            } finally {
                this.loading = false
            }
        },

        extractImportErrors(payload) {
            if (!payload) return []
            if (Array.isArray(payload)) return payload
            if (Array.isArray(payload.errors)) return payload.errors
            if (Array.isArray(payload.rows)) {
                return payload.rows.filter(row => row && (row.error || row.message || row.details))
            }
            if (payload.error && Array.isArray(payload.error.errors)) return payload.error.errors
            if (payload.details && Array.isArray(payload.details.errors)) return payload.details.errors
            return []
        },

        extractErrorMessage(err) {
            const data = err && err.response && err.response.data
            return (
                data && data.error && data.error.message ||
                data && data.message ||
                data && data.content && data.content.error && data.content.error.message ||
                data && data.content && data.content.message ||
                data && data.details && JSON.stringify(data.details) ||
                err && err.message ||
                'Erro ao enviar importação'
            )
        },

        formatImportError(error) {
            if (!error) return '-'
            return error.error || error.message || (error.details ? JSON.stringify(error.details) : '-')
        },

        async fetchImportDetails(id) {
            try {
                const res = await axios.get(`/api/admin/creditor-claims/imports/${id}`)
                const imp = res.data && res.data.content ? res.data.content : res.data
                this.importErrors = this.extractImportErrors(imp)
                this.importResult = imp
                if (imp.client_id) this.form.client_id = imp.client_id
                if (imp.version) this.form.version = imp.version
            } catch (err) {
                console.error('fetchImportDetails', err)
                if (this.$dialog && this.$dialog.notify && this.$dialog.notify.error) {
                    this.$dialog.notify.error('Não foi possível buscar detalhes', { position: 'top-right', timeout: 5000 })
                } else {
                    console.error('Não foi possível buscar detalhes')
                }
            }
        }
    },
};
</script>

<style lang="scss" scoped>
.import-card {
    padding: 20px 24px;
}

.import-card--error {
    border-color: rgba(201, 74, 74, 0.4) !important;
}

.import-card__title {
    font-size: 16px;
    font-weight: 700;
    color: var(--v-primary-base);
}

.import-card__hint {
    font-size: 13px;
    color: var(--v-muted-base);
}

.import-info {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px 24px;
}

.import-info__item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.import-info__label {
    font-size: 12px;
    color: var(--v-muted-base);
}

.import-info__value {
    font-size: 15px;
    color: var(--v-ink-base);
    overflow-wrap: anywhere;
}

@media (max-width: 600px) {
    .import-info {
        grid-template-columns: 1fr;
    }
}
</style>
